import React, { useRef, useState, useEffect } from 'react';
import PropertyBase from './PropertyBase';
import TextVariables from '../../classes/TextVariables';
import DropDownLayer from '../layers/DropdownLayer';
import Input from '../../classes/Input'
import DomUtils from '../../classes/utilities/DomUtils';
import { DropDownList } from '../layout/DropDownList';
import AnalyticsManager from '../../classes/AnalyticsManager';
import { KeyCodes } from '../../interfaces/Input';

export default function TextProperty(props)
{
	const inputRef = useRef();
	const wrapperRef = useRef();

	const [isFocused, setIsFocused] = useState(false);
	const [autocompleteOptions, setAutocompleteOptions] = useState(null);
	const [currentWord, setCurrentWord] = useState(null);

	const contentHeightLimit = useRef(200);
	const rootRect = useRef(null);
	const drawParams = useRef(null);

	useEffect(() =>
	{
		if(props.readonly && inputRef.current)
		{
			const textArea = inputRef.current;
			textArea.setAttribute("style", "height:" + (Math.min(textArea.scrollHeight, 100)) + "px;");
		}
	}, []);

	useEffect(() =>
	{
		if (inputRef.current && props.autoFocus)
		{
			inputRef.current.focus();
		}
	}, [props.autoFocus]);

	useEffect(() =>
	{
		if (isFocused)
		{
			//inputRef.current.setSelectionRange(0, inputRef.current.value.length);
		}
	}, [isFocused]);

	useEffect(() =>
	{
		if(currentWord === null)
		{
			closeList();
			return;
		}

		const variables = TextVariables.autoComplete(currentWord, props.includePartsVariables);
		if(variables && variables.length > 0)
		{
			let dropDownOptions = [];
			for(let vari of variables)
			{
				dropDownOptions.push({ display:vari, value: vari });
			}
			setAutocompleteOptions(dropDownOptions);
		}
		else if(autocompleteOptions && autocompleteOptions.length > 0)
		{
			setAutocompleteOptions(null);
		}
	}, [currentWord]);

	useEffect(() =>
	{
		if (!autocompleteOptions || autocompleteOptions.length <= 0)
		{
			closeList();
		}
		else
		{
			const scrollOffset = DomUtils.getScrollOffsets(inputRef.current.closest('.listViewContainer'));
			rootRect.current = { ...DomUtils.getDomElementRect(inputRef.current) };
			rootRect.current.bottom -= scrollOffset.y;
			rootRect.current.left -= scrollOffset.x;

			let contentMinimumHeight = Math.min(3, autocompleteOptions.length) * 20;
			if (props.searchable)
				contentMinimumHeight += 20;
			if (props.decorators)
				contentMinimumHeight += 20;

			var coverRect = DomUtils.getDomElementRect(document.getElementById('workspaceRoot'));
			if(!coverRect) coverRect ={ yMax:949 }

			contentHeightLimit.current = coverRect.yMax - rootRect.current.yMax;
			contentHeightLimit.current = /*contentHeightLimit.current < 40 ? 40 : */contentHeightLimit.current > 250 ? 250 : contentHeightLimit.current;

			drawParams.current = {
				left: (rootRect.current.left - 1) + "px",
				width: (rootRect.current.width) + "px",
			};

			if (rootRect.current.bottom + contentMinimumHeight > coverRect.yMax)
			{
				drawParams.current.inverse = true;
				drawParams.current.top = (rootRect.current.bottom + 2 - rootRect.current.height - contentMinimumHeight) + "px";
				contentHeightLimit.current = contentMinimumHeight;
			}
			else
			{
				drawParams.current.top = (rootRect.current.bottom - 2) + "px";
			}

			let dropdownList = buildDropdownList();

			document.addEventListener('mousedown', testClickOut);
			document.addEventListener('keyup', testKeyOut);

			if(dropdownList)
			{
				DropDownLayer.setDropdownList(dropdownList);
			}
		}
	}, [autocompleteOptions]);

	function testClickOut(e)
	{
		const nearestRoot = e.target?.closest(".dropDownListWrapper");
		if (nearestRoot !== wrapperRef.current)
		{
			closeList();
		}
	}

	function testKeyOut(e)
	{
		if (e.keyCode === KeyCodes.ESCAPE)
		{
			closeList();
		}
	}

	function closeList()
	{
		document.removeEventListener('mousedown', testClickOut);
		document.removeEventListener('keyup', testKeyOut);
		DropDownLayer.setDropdownList(null);
		setAutocompleteOptions(null);
		setCurrentWord(null);
	}

	function buildDropdownList()
	{
		if(!rootRect.current) return;
		return <div className={"dropDownListWrapper"}
			ref={wrapperRef}
			data-name={"drop-down-list-wrapper"}
			style={{
				...drawParams.current,
				zIndex: 100000000,
			}}
		>
			<DropDownList
				data-name={props["data-name"] + "-options-container"}
				// searchable={props.searchable}
				options={autocompleteOptions}
				selected={props.value}
				onItemToggled
				onSelectionChanged={(selection) =>
				{
					applySelectionToText(selection);
					closeList();
				}}
				maxHeight={contentHeightLimit.current}
				decorators={props.decorators}
				inverse={drawParams.current ? drawParams.current.inverse ? drawParams.current.inverse : false : false}
			/>
		</div>
	}

	function applySelectionToText(selection)
	{
		let textObject = TextVariables.breakToWords(props.value);
		let wordIndex = 0;
		for(wordIndex; wordIndex < textObject.words.length; wordIndex++)
		{
			if(textObject.words[wordIndex] === currentWord)
				break;
		}
		textObject.words[wordIndex] = selection;

		if (props.onChangeCallback)
			props.onChangeCallback(TextVariables.buildFromWords(textObject.words, textObject.splitters));

		closeList();
		inputRef.current.focus();
	}

	const readonly = props.readOnly ? props.readOnly : false;

	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-text-property";

	const rows = props.rows ?? 2;

	return (
		<PropertyBase {...props}
			data-name={dataName + "-root"}
		>
			<textarea className={["inputRoot textArea", props.className].join(' ')}
				data-name={dataName + "-input"}
				ref={inputRef}
				cols={37}
				rows={rows}
				placeholder={props.placeholder !== undefined ? props.placeholder : props.value}
				value={props.multivalues ? "~" : props.value}
				readOnly={readonly}
				disabled={props.disabled}
				spellCheck="false"
				style={props.initialHeight ? { height: props.initialHeight } : {}}
				onDragStart={(e)=>
				{
					e.preventDefault();
					return false;
				}}
				onChange={(e) =>
				{
					if(props.allowTextVariables)
					{
						setCurrentWord(TextVariables.getCurrentWord(e.target.value, e.target.selectionStart));
					}
					if (props.onChangeCallback)
						props.onChangeCallback(e.target.value);
				}}
				onFocus={(e) =>
				{
					setIsFocused(true);
					if (props.onFocus){props.onFocus(inputRef.current);}
					if (!readonly && props.onFocusCallback)
						props.onFocusCallback();
				}}
				onBlur={(e) =>
				{
					if(autocompleteOptions && autocompleteOptions.length > 0) return;
					if (props.onBlur){props.onBlur(inputRef.current);}
					if (!readonly && props.onCommitCallback)
					{
						AnalyticsManager.managedEvent('propertyChanged', 'propertyId', e.target.dataset.name);
						const valueToCommit =
							e.target.value !== "" ? e.target.value :
							  props.placeholder !== undefined ? props.placeholder :
							    props.value !== undefined ? props.value :
							      e.target.value;

						props.onCommitCallback(valueToCommit);
					}

					setIsFocused(false);
				}}
				onKeyPress={(e) =>
				{
					if (e.keyCode === KeyCodes.ESCAPE)
						e.target.blur(e.target.value);

				}}
				onKeyDownCapture={(e)=>
				{
					if(autocompleteOptions && autocompleteOptions.length > 0)
					{
						if(e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter")
						{
							e.preventDefault();
						}
					}
				}}
			/>
		</PropertyBase>
	);
}
